import api from './api';
import type {
  Survey,
  CreateSurveyDto,
  Question,
  CreateQuestionDto,
  QuestionOption,
  CreateOptionDto,
  QuestionCategory,
  SurveyResponse,
  SubmitResponseDto,
  SurveyAnalytics,
  SurveyInvitation,
  QuestionType,
  SurveyDraft,
  SaveDraftRequest,
  DraftResponse,
} from '../types';

// DTO for creating/updating categories
interface CreateCategoryDto {
  name: string;
  description?: string;
  displayOrder?: number;
}

interface PagedResult<T> {
  items: T[];
  totalCount: number;
  pageNumber: number;
  pageSize: number;
}

export const surveyService = {
  // Survey CRUD
  async getSurveys(): Promise<Survey[]> {
    const response = await api.get<PagedResult<Survey>>('/surveys');
    return response.data.items;
  },

  async getSurvey(id: number): Promise<Survey> {
    const response = await api.get<Survey>(`/surveys/${id}`);
    return response.data;
  },

  async createSurvey(data: CreateSurveyDto): Promise<Survey> {
    const response = await api.post<Survey>('/surveys', data);
    return response.data;
  },

  async updateSurvey(id: number, data: Partial<CreateSurveyDto>): Promise<Survey> {
    const response = await api.put<Survey>(`/surveys/${id}`, data);
    return response.data;
  },

  async deleteSurvey(id: number): Promise<void> {
    await api.delete(`/surveys/${id}`);
  },

  async duplicateSurvey(id: number): Promise<Survey> {
    const response = await api.post<Survey>(`/surveys/${id}/duplicate`);
    return response.data;
  },

  // Question CRUD
  async getQuestions(surveyId: number): Promise<Question[]> {
    const response = await api.get<Question[]>(`/surveys/${surveyId}/questions`);
    return response.data;
  },

  async createQuestion(data: CreateQuestionDto): Promise<Question> {
    const response = await api.post<Question>(`/surveys/${data.surveyId}/questions`, data);
    return response.data;
  },

  async updateQuestion(id: number, data: Partial<CreateQuestionDto>): Promise<Question> {
    const response = await api.put<Question>(`/questions/${id}`, data);
    return response.data;
  },

  async deleteQuestion(id: number): Promise<void> {
    await api.delete(`/questions/${id}`);
  },

  async reorderQuestions(surveyId: number, questionIds: number[]): Promise<void> {
    // Backend expects: { questions: [{ questionId, displayOrder }, ...] }
    const questions = questionIds.map((id, index) => ({
      questionId: id,
      displayOrder: index + 1,
    }));
    await api.put(`/surveys/${surveyId}/questions/reorder`, { questions });
  },

  // Option CRUD
  async getOptions(questionId: number): Promise<QuestionOption[]> {
    const response = await api.get<QuestionOption[]>(`/questions/${questionId}/options`);
    return response.data;
  },

  async createOption(data: CreateOptionDto): Promise<QuestionOption> {
    const response = await api.post<QuestionOption>(`/questions/${data.questionId}/options`, data);
    return response.data;
  },

  async updateOption(id: number, data: Partial<CreateOptionDto>): Promise<QuestionOption> {
    const response = await api.put<QuestionOption>(`/options/${id}`, data);
    return response.data;
  },

  async deleteOption(id: number): Promise<void> {
    await api.delete(`/options/${id}`);
  },

  // Category CRUD (Sections)
  async getCategories(surveyId: number): Promise<QuestionCategory[]> {
    const response = await api.get<QuestionCategory[]>(`/surveys/${surveyId}/categories`);
    return response.data;
  },

  async createCategory(surveyId: number, data: CreateCategoryDto): Promise<QuestionCategory> {
    const response = await api.post<QuestionCategory>(`/surveys/${surveyId}/categories`, data);
    return response.data;
  },

  async updateCategory(id: number, data: Partial<CreateCategoryDto>): Promise<QuestionCategory> {
    const response = await api.put<QuestionCategory>(`/categories/${id}`, data);
    return response.data;
  },

  async deleteCategory(id: number): Promise<void> {
    await api.delete(`/categories/${id}`);
  },

  // Question Types
  async getQuestionTypes(): Promise<QuestionType[]> {
    const response = await api.get<QuestionType[]>('/question-types');
    return response.data;
  },

  // Responses
  async getResponses(surveyId: number): Promise<SurveyResponse[]> {
    const response = await api.get<SurveyResponse[]>(`/surveys/${surveyId}/responses`);
    return response.data;
  },

  async getSurveyResponses(
    surveyId: number,
    page: number = 1,
    pageSize: number = 10
  ): Promise<{ items: SurveyResponse[]; totalCount: number }> {
    const response = await api.get<{ items: SurveyResponse[]; totalCount: number }>(
      `/surveys/${surveyId}/responses`,
      { params: { page, pageSize } }
    );
    return response.data;
  },

  async getResponse(id: number): Promise<SurveyResponse> {
    const response = await api.get<SurveyResponse>(`/responses/${id}`);
    return response.data;
  },

  async deleteResponse(id: number): Promise<void> {
    await api.delete(`/responses/${id}`);
  },

  // Analytics
  async getAnalytics(surveyId: number): Promise<SurveyAnalytics> {
    const response = await api.get<SurveyAnalytics>(`/surveys/${surveyId}/analytics`);
    return response.data;
  },

  async exportResponses(surveyId: number, format: 'csv' | 'excel' | 'spss' = 'csv'): Promise<Blob> {
    const response = await api.get(`/surveys/${surveyId}/export`, {
      params: { format },
      responseType: 'blob',
    });
    return response.data;
  },

  // Invitations
  async getInvitations(surveyId: number): Promise<SurveyInvitation[]> {
    const response = await api.get<SurveyInvitation[]>(`/surveys/${surveyId}/invitations`);
    return response.data;
  },

  async createInvitation(surveyId: number, data: { email?: string; maxUses?: number; expiresDate?: string }): Promise<SurveyInvitation> {
    const response = await api.post<SurveyInvitation>(`/surveys/${surveyId}/invitations`, data);
    return response.data;
  },

  async deleteInvitation(id: number): Promise<void> {
    await api.delete(`/invitations/${id}`);
  },

  async sendInvitationEmail(id: number): Promise<void> {
    await api.post(`/invitations/${id}/send-email`);
  },
};

// Public survey service (no auth required)
export const publicSurveyService = {
  async getSurvey(token: string): Promise<Survey> {
    const response = await api.get<Survey>(`/public/surveys/${token}`);
    return response.data;
  },

  async canSubmit(token: string): Promise<boolean> {
    const response = await api.get<{ canSubmit: boolean }>(`/public/surveys/${token}/can-submit`);
    return response.data.canSubmit;
  },

  async checkSubmissionStatus(token: string, anonymousToken?: string): Promise<{
    hasSubmitted: boolean;
    allowMultipleSubmissions: boolean;
    canSubmit: boolean;
  }> {
    const response = await api.get<{
      hasSubmitted: boolean;
      allowMultipleSubmissions: boolean;
      canSubmit: boolean;
    }>(`/public/surveys/${token}/submission-status`, {
      headers: anonymousToken ? { 'X-Anonymous-Token': anonymousToken } : undefined,
    });
    return response.data;
  },

  async startResponse(token: string, anonymousToken?: string): Promise<SurveyResponse> {
    const response = await api.post<SurveyResponse>(`/public/surveys/${token}/responses`, null, {
      headers: anonymousToken ? { 'X-Anonymous-Token': anonymousToken } : undefined,
    });
    return response.data;
  },

  async submitResponse(token: string, responseId: number, data: Omit<SubmitResponseDto, 'surveyId'>): Promise<SurveyResponse> {
    const response = await api.put<SurveyResponse>(`/public/surveys/${token}/responses/${responseId}`, data);
    return response.data;
  },

  // Draft Management
  async getDraft(token: string, anonymousToken?: string): Promise<SurveyDraft | null> {
    try {
      const response = await api.get<SurveyDraft>(`/public/surveys/${token}/drafts`, {
        headers: anonymousToken ? { 'X-Anonymous-Token': anonymousToken } : undefined,
      });
      return response.data;
    } catch (error) {
      // 404 means no draft exists, which is not an error
      if ((error as { response?: { status: number } }).response?.status === 404) {
        return null;
      }
      throw error;
    }
  },

  async saveDraft(token: string, data: SaveDraftRequest, anonymousToken?: string): Promise<DraftResponse> {
    const response = await api.post<DraftResponse>(`/public/surveys/${token}/drafts`, data, {
      headers: anonymousToken ? { 'X-Anonymous-Token': anonymousToken } : undefined,
    });
    return response.data;
  },

  async deleteDraft(token: string, draftId: number, anonymousToken?: string): Promise<void> {
    await api.delete(`/public/surveys/${token}/drafts/${draftId}`, {
      headers: anonymousToken ? { 'X-Anonymous-Token': anonymousToken } : undefined,
    });
  },
};

export default surveyService;
